/**
 * Karte „Geräte" (Phase W, Kapitel 3.5, Entscheidung #32) — Liste aktiver
 * Sitzungen mit Geräte-Label und „zuletzt aktiv vor X", die aktuelle Sitzung
 * ist markiert. „Überall abmelden" widerruft alle Sitzungen außer der
 * aktuellen.
 *
 * Bewusst KEIN Einzel-Abmelden pro Gerät (Konzept-Grundsatz „so einfach wie
 * möglich") — der Anwendungsfall ist praktisch immer „ich weiß nicht mehr,
 * wo ich noch angemeldet bin, mach einfach überall außer hier zu."
 */

import { useState } from 'react';
import { useSessions, useRevokeOtherSessions } from '../data/sessions';
import { hapticError, hapticSuccess } from '../lib/haptics';

function relativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return 'gerade eben';
  if (minutes < 60) return `vor ${minutes} Min.`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `vor ${hours} Std.`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `vor ${days} Tag${days === 1 ? '' : 'en'}`;
  const months = Math.floor(days / 30);
  return `vor ${months} Monat${months === 1 ? '' : 'en'}`;
}

export function DevicesCard() {
  const { data: sessions, isLoading } = useSessions();
  const revokeOthers = useRevokeOtherSessions();
  const [error, setError] = useState<string | null>(null);

  const list = sessions ?? [];
  const otherCount = list.filter((s) => !s.isCurrent).length;

  async function onRevokeOthers() {
    if (!confirm('Wirklich überall außer auf diesem Gerät abmelden?')) return;
    setError(null);
    try {
      await revokeOthers.mutateAsync();
      hapticSuccess();
    } catch {
      hapticError();
      setError('Abmelden fehlgeschlagen — bitte erneut versuchen.');
    }
  }

  return (
    <div className="card p-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-medium">Geräte</p>
        {otherCount > 0 && (
          <button
            onClick={() => void onRevokeOthers()}
            disabled={revokeOthers.isPending}
            className="btn-ghost text-xs px-3 py-1.5 shrink-0 disabled:opacity-50"
          >
            {revokeOthers.isPending ? '…' : 'Überall abmelden'}
          </button>
        )}
      </div>

      {isLoading && <p className="text-xs text-slate-500">Lädt…</p>}

      {!isLoading && list.length === 0 && (
        <p className="text-xs text-slate-500">Keine aktiven Sitzungen gefunden.</p>
      )}

      <ul className="space-y-1.5">
        {list.map((s) => (
          <li key={s.id} className="flex items-center justify-between gap-3 text-xs">
            <span className="text-slate-300 truncate">{s.deviceLabel ?? 'Unbekanntes Gerät'}</span>
            <span className="text-slate-500 shrink-0">
              {s.isCurrent ? 'dieses Gerät' : `zuletzt aktiv ${relativeTime(s.lastActiveAt)}`}
            </span>
          </li>
        ))}
      </ul>

      {error && <p className="text-xs text-red-400">{error}</p>}
    </div>
  );
}
